import React, { createContext, useCallback, useEffect, useMemo } from "react";

import { useParams } from "react-router-dom";

import Loader from "components/ui/Loader";
import ErrorHandler from "components/ui/ErrorHandler";

import { useFetchProtocolById, useFetchResponseFields } from "../services";
import { recursivelyChangeOptionsToChoices } from "features/protocols/helper";

export const ProtocolContext = createContext({
  protocol: {},
  setProtocol: () => {},
});
const ProtocolProvider = ({ children }) => {
  const { protocolId } = useParams();

  const { isLoading, error, protocol, setProtocol } = useFetchProtocolById({
    protocolId,
  });

  const {
    isLoading: isLoadingResponseFields,
    error: errorResponseFields,
    responseFields,
    fetchResponseFields,
  } = useFetchResponseFields({
    perPage: 50,
    protocolId,
    allResponseFields: false,
  });

  const updatedProtocol = useCallback(() => {
    setProtocol((prevProtocol) => ({
      ...prevProtocol,
      flexibleFormFields: responseFields.map((field) => ({
        ...field,
        choices: recursivelyChangeOptionsToChoices(field?.options),
      })),
    }));
  }, [setProtocol, responseFields]);

  const memoizedValues = useMemo(
    () => ({
      protocol,
      setProtocol,
      responseFields,
      fetchResponseFields,
      updatedProtocol,
    }),
    [
      protocol,
      setProtocol,
      responseFields,
      fetchResponseFields,
      updatedProtocol,
    ],
  );

  useEffect(() => {
    updatedProtocol();
  }, [updatedProtocol]);

  if (isLoading || isLoadingResponseFields) return <Loader />;
  if (error || errorResponseFields)
    return <ErrorHandler error={error || errorResponseFields} />;

  if (!protocol) return <></>;

  return (
    <ProtocolContext.Provider value={memoizedValues}>
      {children}
    </ProtocolContext.Provider>
  );
};

export default ProtocolProvider;
